import { useState } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MapPin, Phone, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactSchema = z.object({
  name: z.string().min(2, "Name ist erforderlich"),
  email: z.string().email("Gültige E-Mail-Adresse erforderlich"),
  phone: z.string().optional(),
  subject: z.string().min(1, "Bitte wählen Sie ein Anliegen"),
  message: z.string().min(10, "Nachricht muss mindestens 10 Zeichen lang sein"),
  consent: z.boolean().refine(val => val === true, "Zustimmung erforderlich")
});

const ContactSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      consent: false
    }
  });

  const consent = watch("consent");
  const subject = watch("subject");

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data);
      setSubmitSuccess(true);
      toast.success("Vielen Dank für Ihre Nachricht!");
      reset();
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="kontakt" 
      className="py-24 md:py-32 bg-white"
      data-testid="contact-section"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-komel-primary mb-4" data-testid="contact-title">
            Kontakt
          </h2>
          <div className="section-divider" />
          <p className="text-lg text-komel-secondary max-w-2xl mx-auto">
            Kontaktieren Sie uns für ein unverbindliches Erstgespräch. Wir freuen uns auf Ihr Projekt.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {submitSuccess ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-semibold text-komel-primary mb-2">
                  Vielen Dank!
                </h3>
                <p className="text-komel-secondary mb-6">
                  Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns zeitnah bei Ihnen.
                </p>
                <Button
                  onClick={() => setSubmitSuccess(false)}
                  variant="outline"
                  className="border-komel-primary text-komel-primary hover:bg-komel-primary hover:text-white"
                  data-testid="contact-new-message"
                >
                  Neue Anfrage senden
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-komel-primary font-medium">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    {...register("name")}
                    placeholder="Ihr Name"
                    className={`mt-2 rounded-none ${errors.name ? 'border-red-500' : ''}`}
                    data-testid="contact-name"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-komel-primary font-medium">
                    E-Mail *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    {...register("email")}
                    placeholder="ihre@email.ch"
                    className={`mt-2 rounded-none ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="contact-email"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-komel-primary font-medium">
                    Telefon (optional)
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    {...register("phone")}
                    placeholder="+41 71 000 00 00"
                    className="mt-2 rounded-none"
                    data-testid="contact-phone"
                  />
                </div>

                {/* Subject */}
                <div>
                  <Label htmlFor="subject" className="text-komel-primary font-medium">
                    Anliegen *
                  </Label>
                  <Select value={subject} onValueChange={(val) => setValue("subject", val, { shouldValidate: true })}>
                    <SelectTrigger className={`mt-2 rounded-none ${errors.subject ? 'border-red-500' : ''}`} data-testid="contact-subject">
                      <SelectValue placeholder="Bitte wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Offerte">Offerte anfragen</SelectItem>
                      <SelectItem value="Rueckruf">Rückruf gewünscht</SelectItem>
                      <SelectItem value="Beratung">Beratungsgespräch</SelectItem>
                      <SelectItem value="Referenzen">Referenzen besichtigen</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.subject && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.subject.message}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-komel-primary font-medium">
                    Nachricht *
                  </Label>
                  <Textarea
                    id="message"
                    {...register("message")}
                    placeholder="Beschreiben Sie Ihr Anliegen..."
                    rows={5}
                    className={`mt-2 rounded-none resize-none ${errors.message ? 'border-red-500' : ''}`}
                    data-testid="contact-message"
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.message.message}
                    </p>
                  )}
                </div>

                {/* Consent */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={consent}
                    onCheckedChange={(checked) => setValue("consent", checked)}
                    className="mt-1"
                    data-testid="contact-consent"
                  />
                  <Label htmlFor="consent" className="text-sm text-komel-secondary leading-relaxed cursor-pointer">
                    Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage verwendet werden. *
                  </Label>
                </div>
                {errors.consent && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.consent.message}
                  </p>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-komel-accent hover:bg-komel-accent-hover text-white font-semibold py-6 rounded-none transition-all duration-300 disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Wird gesendet...
                    </>
                  ) : (
                    "Anfrage senden"
                  )}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Contact Details */}
            <div className="bg-komel-neutral-100 p-8" data-testid="contact-info">
              <h3 className="font-heading text-2xl font-semibold text-komel-primary mb-6">
                Komel AG
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-komel-primary rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-komel-primary">Adresse</p>
                    <p className="text-komel-secondary">
                      Multergasse 2a<br />
                      9000 St. Gallen<br />
                      Schweiz
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-komel-primary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-komel-primary">Telefon</p>
                    <a 
                      href="tel:+41712261199"
                      className="text-komel-accent hover:text-komel-accent-hover transition-colors text-lg font-medium"
                      data-testid="contact-phone-link"
                    >
                      071 226 11 99
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="aspect-[4/3] w-full" data-testid="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2694.4397896961!2d9.3734!3d47.4244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479b1e36f9e0f8ad%3A0x400ae6bcacc90!2sMultergasse%202a%2C%209000%20St.%20Gallen%2C%20Schweiz!5e0!3m2!1sde!2sch!4v1700000000000!5m2!1sde!2sch"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Komel AG Standort"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
